import { useState, useEffect } from 'react';
import { accountsAPI, billsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Receipt, Zap, Wifi, Home, Car, GraduationCap, Heart } from 'lucide-react';
import { toast } from 'sonner';
import TransferConfirmation from '../components/TransferConfirmation';

const CATEGORIES = [
  { value: 'Utilities', label: 'Utilities', icon: Zap },
  { value: 'Internet', label: 'Internet & Cable', icon: Wifi },
  { value: 'Rent', label: 'Rent / Mortgage', icon: Home },
  { value: 'Insurance', label: 'Insurance', icon: Car },
  { value: 'Education', label: 'Education', icon: GraduationCap },
  { value: 'Healthcare', label: 'Healthcare', icon: Heart },
  { value: 'Other', label: 'Other', icon: Receipt },
];

export default function BillPayPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromAccount, setFromAccount] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Utilities');
  const [memo, setMemo] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountsAPI.list();
      setAccounts(res.data.accounts);
      if (res.data.accounts.length > 0) setFromAccount(res.data.accounts[0]._id);
    } catch (err) {
      toast.error('Failed to load accounts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      await billsAPI.pay({
        from_account_id: fromAccount,
        payee_name: payeeName,
        amount: parseFloat(amount),
        category,
        memo
      });
      setConfirmDetails({
        type: 'Bill Payment',
        amount: parseFloat(amount),
        recipient: payeeName,
        fromAccount: selectedAccount?.name || 'Account',
      });
      setConfirmOpen(true);
      setPayeeName(''); setAmount(''); setMemo('');
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = accounts.find(a => a._id === fromAccount);

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Pay Bills</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Make a Payment</CardTitle>
                <p className="text-sm text-muted-foreground">Pay your bills securely</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Pay From</Label>
                <Select value={fromAccount} onValueChange={setFromAccount}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(a => (
                      <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedAccount && <p className="text-xs text-muted-foreground">Available: {formatCurrency(selectedAccount.available_balance)}</p>}
              </div>
              <div className="space-y-2">
                <Label>Payee Name</Label>
                <Input data-testid="billpay-payee-input" placeholder="Electric Company, AT&T, etc." value={payeeName} onChange={e => setPayeeName(e.target.value)} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (USD)</Label>
                  <Input type="number" step="0.01" min="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Memo</Label>
                <Textarea placeholder="Invoice #, account reference, etc." value={memo} onChange={e => setMemo(e.target.value)} />
              </div>
              <Button data-testid="billpay-submit-payment-button" type="submit" className="w-full" disabled={loading}>
                {loading ? 'Processing...' : 'Pay Bill'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Quick Pay Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Common Billers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left transition-colors"
                  onClick={() => setCategory(cat.value)}
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <cat.icon className="h-4 w-4 text-gray-600" />
                  </div>
                  <span className="text-sm font-medium">{cat.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <TransferConfirmation open={confirmOpen} onClose={setConfirmOpen} transferDetails={confirmDetails} />
    </div>
  );
}